import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import CardSession from "../../../../components/individual-session/CardSession";
import { StarIcon } from "@heroicons/react/outline";
import { StarIcon as FilledStarIcon } from "@heroicons/react/solid";
import { makeApiRequest } from "../../../../helpers";
import CommentsTab from "./CommentsTab";
import NotesTab from "./NotesTab";
import TabsComponent from "../../../../components/tabs/TabsComponent";
import { ApiResponse } from "../../../../api/apiResponse";
import SessionModel, {
  SessionServiceProvider,
} from "../../../../api/models/SessionModel";
import { getSessionData } from "../../../../api/endpoints/supervisors/sessions";
import { FullSpinner } from "../../../../components/LoadingSpinner";
import SessionRatingModel, {
  SessionRatingLevels,
} from "../../../../api/models/SessionRatingModel";

export default function IndividualSession() {
  const LocalBasePath = "page.supervisor.sessions";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState<SessionModel | null>(null);
  const [studentRating, setStudentRating] =
    useState<SessionRatingModel | undefined>();
  const [tutorRating, setTutorRating] =
    useState<SessionRatingModel | undefined>();
  const { id } = useParams();
  const ratingValues: any = {
    unset: {
      coloredStars: 0,
      unColoredStars: 4,
    },
    bad: {
      coloredStars: 1,
      unColoredStars: 3,
    },
    neutral: {
      coloredStars: 2,
      unColoredStars: 2,
    },
    good: {
      coloredStars: 4,
      unColoredStars: 0,
    },
  };

  const getSessionInfo = () => {
    makeApiRequest(
      () => getSessionData(id ?? ""),
      (res: ApiResponse) => {
        const sessionInfo = new SessionModel(res.data);
        setSession(sessionInfo);
        setStudentRating(sessionInfo.studentRating);
        setTutorRating(sessionInfo.tutorRating);
      }
    );
  };

  const tabs = [
    {
      name: t("comments"),
      content: <CommentsTab session={session} />,
    },
    {
      name: t("notes"),
      content: <NotesTab session={session} />,
    },
  ];

  useEffect(() => {
    if (!!id) getSessionInfo();
  }, []);

  if (!session) {
    return <FullSpinner size={300} />;
  }

  return (
    <div className="w-full  flex flex-col gap-8 py-3">
      <CardSession session={session} />
      <div className="flex  flex-col w-full md:flex-row items-start gap-6  md:px-6 h-fit">
        <div className="md:flex-1 w-full h-full md:w-auto">
          <VideoPlayer
            url={session!.recordingUrl}
            noVideoPlaceholder={t("no_video")}
            serviceProvider={session!.serviceProvider}
          />
        </div>
        <div className="flex flex-row justify-between md:flex-col gap-6 w-full  px-4 md:px-0 md:w-[256px] ">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col ">
              <QuestionStarRatingBar
                key="studentComprehension"
                answer={studentRating?.comprehensionLevel ?? "unset"}
              />
              <p>{t("studentComprehension")}</p>
            </div>
            <div className="flex flex-col ">
              <QuestionStarRatingBar
                key="tutorPerformance"
                answer={studentRating?.tutorPerformance ?? "unset"}
              />
              <p>{t("tutorPerformance")}</p>
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col  ">
              <QuestionStarRatingBar
                key="tutorPerformance"
                answer={studentRating?.connectionQuality ?? "unset"}
              />
              <p>{t("studentConnectionQuality")}</p>
            </div>
            <div className="flex flex-col ">
              <QuestionStarRatingBar
                key="tutorPerformance"
                answer={tutorRating?.connectionQuality ?? "unset"}
              />
              <p>{t("tutorConnectionQuality")}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full">
        <TabsComponent
          requests={true}
          defaultTab={+(searchParams.get("tab") ?? 0)}
          tabs={tabs}
        />
      </div>
    </div>
  );
}

type QuestionStarRatingBarProps = {
  key: string;
  answer: SessionRatingLevels;
};

function QuestionStarRatingBar(props: QuestionStarRatingBarProps) {
  const { answer, key } = props;
  const ratingValues: any = {
    unset: {
      coloredStars: 0,
      unColoredStars: 4,
    },
    bad: {
      coloredStars: 1,
      unColoredStars: 3,
    },
    neutral: {
      coloredStars: 2,
      unColoredStars: 2,
    },
    good: {
      coloredStars: 4,
      unColoredStars: 0,
    },
  };

  const coloredCount = ratingValues[answer].coloredStars;
  const emptyCount = ratingValues[answer].unColoredStars;

  return (
    <StarRatingBar
      coloredStarsCount={coloredCount}
      emptyStartsCount={emptyCount}
      key={key}
    />
  );
}

type StarRatingBarProps = {
  coloredStarsCount: number;
  emptyStartsCount: number;
  key: string;
};

function StarRatingBar(props: StarRatingBarProps) {
  const { coloredStarsCount, emptyStartsCount, key } = props;
  const coloredArray = Array(coloredStarsCount);
  const emptyArray = Array(emptyStartsCount);
  return (
    <div className="flex items-center">
      {Array.apply(null, coloredArray).map((_, index) => (
        <FilledStarIcon
          key={`${key}-colored-${index}`}
          className="text-yellow-400 h-[1.35rem] w-[1.35rem] flex-shrink-0"
          aria-hidden="true"
        />
      ))}
      {Array.apply(null, emptyArray).map((_, index) => (
        <StarIcon
          key={`${key}-empty-${index}`}
          className="text-gray-200 h-[1.25rem] w-[1.25rem] flex-shrink-0"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

type VideoPlayerProps = {
  url?: string;
  noVideoPlaceholder: string;
  serviceProvider: SessionServiceProvider;
};

// TODO: This needs to be refactored because the url should always be a string and the null case should be handled outside the video player component
function VideoPlayer(props: VideoPlayerProps) {
  const { url, noVideoPlaceholder, serviceProvider } = props;
  if (!url) {
    return (
      <div className="w-full h-full flex justify-center items-center min-h-[200px]">
        <h1>{noVideoPlaceholder}</h1>
      </div>
    );
  }
  if (serviceProvider == "jitsi") {
    return JitsiVideoPlayer(url);
  }
  return BBBVideoPlayer(url);
}

function BBBVideoPlayer(url: string) {
  return (
    <iframe
      title="Sanad"
      key={url}
      width="100%"
      height="100%"
      style={{ minHeight: "500px" }}
      src={url}
      allowFullScreen
    ></iframe>
  );
}

function JitsiVideoPlayer(url: string) {
  return (
    <video controls preload="none" style={{ width: "100%" }}>
      <source src={url} type="video/mp4" />
    </video>
  );
}
